
import { createContext, useContext, useReducer, useEffect } from 'react';

// Tipos de acciones del carrito
const CART_ACTIONS = {
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_QUANTITY: 'UPDATE_QUANTITY',
    CLEAR_CART: 'CLEAR_CART',
    LOAD_CART: 'LOAD_CART'
};

// Estado inicial del carrito
const initialState = {
    items: [],
    total: 0,
    itemCount: 0,
    isOpen: false
};

// Reducer del carrito
function cartReducer(state, action) {
    switch (action.type) {
        case CART_ACTIONS.ADD_ITEM: {
            const { producto, cantidad = 1 } = action.payload;
            const existingItemIndex = state.items.findIndex(item => item.id === producto.id);
            
            let newItems;
            if (existingItemIndex >= 0) {
                // Actualizar cantidad si el producto ya existe
                newItems = state.items.map((item, index) => 
                    index === existingItemIndex 
                        ? { ...item, cantidad: item.cantidad + cantidad }
                        : item
                );
            } else {
                // Agregar nuevo producto
                newItems = [...state.items, {
                    id: producto.id,
                    nombre: producto.nombre,
                    precio_venta: parseFloat(producto.precio_venta),
                    precio: parseFloat(producto.precio_venta), // Mantener compatibilidad
                    imagen: producto.imagen_principal || '/productos/placeholder.png',
                    cantidad: cantidad,
                    stock_disponible: producto.stock_disponible || 0
                }];
            }
            
            const total = newItems.reduce((sum, item) => sum + (item.precio_venta * item.cantidad), 0);
            const itemCount = newItems.reduce((sum, item) => sum + item.cantidad, 0);
            
            return {
                ...state,
                items: newItems,
                total,
                itemCount
            };
        }
        
        case CART_ACTIONS.REMOVE_ITEM: {
            const newItems = state.items.filter(item => item.id !== action.payload.id);
            const total = newItems.reduce((sum, item) => sum + (item.precio_venta * item.cantidad), 0);
            const itemCount = newItems.reduce((sum, item) => sum + item.cantidad, 0);
            
            return {
                ...state,
                items: newItems,
                total,
                itemCount
            };
        }
        
        case CART_ACTIONS.UPDATE_QUANTITY: {
            const { id, cantidad } = action.payload;
            
            if (cantidad <= 0) {
                return cartReducer(state, { type: CART_ACTIONS.REMOVE_ITEM, payload: { id } });
            }
            
            const newItems = state.items.map(item => 
                item.id === id ? { ...item, cantidad } : item
            );
            
            const total = newItems.reduce((sum, item) => sum + (item.precio_venta * item.cantidad), 0);
            const itemCount = newItems.reduce((sum, item) => sum + item.cantidad, 0);
            
            return {
                ...state,
                items: newItems,
                total,
                itemCount
            };
        }
        
        case CART_ACTIONS.CLEAR_CART:
            return {
                ...state,
                items: [],
                total: 0,
                itemCount: 0
            };
            
        case CART_ACTIONS.LOAD_CART:
            // Migrar items antiguos que no tengan precio_venta
            const migratedItems = action.payload.items?.map(item => {
                if (!item.precio_venta && item.precio) {
                    return {
                        ...item,
                        precio_venta: item.precio
                    };
                }
                return item;
            }) || [];
            
            // Recalcular total con precio_venta
            const migratedTotal = migratedItems.reduce((sum, item) => 
                sum + ((item.precio_venta || item.precio || 0) * item.cantidad), 0
            );
            
            return {
                ...state,
                ...action.payload,
                items: migratedItems,
                total: migratedTotal
            };
            
        case 'TOGGLE_CART':
            return {
                ...state,
                isOpen: action.payload
            };
            
        default:
            return state;
    }
}

// Contexto del carrito
const CartContext = createContext();

// Provider del carrito
export function CartProvider({ children }) {
    const [state, dispatch] = useReducer(cartReducer, initialState);
    
    // Cargar carrito desde localStorage al inicializar
    useEffect(() => {
        const savedCart = localStorage.getItem('wins-cart');
        if (savedCart) {
            try {
                const cartData = JSON.parse(savedCart);
                dispatch({ type: CART_ACTIONS.LOAD_CART, payload: cartData });
            } catch (error) {
                console.error('Error loading cart from localStorage:', error);
            }
        }
    }, []);
    
    // Guardar carrito en localStorage cuando cambie
    useEffect(() => {
        if (state.items.length > 0 || state.total > 0) {
            localStorage.setItem('wins-cart', JSON.stringify({
                items: state.items,
                total: state.total,
                itemCount: state.itemCount
            }));
        } else {
            localStorage.removeItem('wins-cart');
        }
    }, [state.items, state.total, state.itemCount]);
    
    // Funciones del carrito
    const addItem = (producto, cantidad = 1) => {
        dispatch({ 
            type: CART_ACTIONS.ADD_ITEM, 
            payload: { producto, cantidad } 
        });
    };
    
    const removeItem = (id) => {
        dispatch({ 
            type: CART_ACTIONS.REMOVE_ITEM, 
            payload: { id } 
        });
    };
    
    const updateQuantity = (id, cantidad) => {
        dispatch({ 
            type: CART_ACTIONS.UPDATE_QUANTITY, 
            payload: { id, cantidad } 
        });
    };
    
    const clearCart = () => {
        dispatch({ type: CART_ACTIONS.CLEAR_CART });
    };
    
    const toggleCart = () => {
        dispatch({ 
            type: 'TOGGLE_CART',
            payload: !state.isOpen 
        });
    };
    
    const value = {
        ...state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        toggleCart
    };
    
    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
}

// Hook para usar el contexto del carrito
export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
}

export default CartContext;
